import { FC, useRef, useState } from 'react';
import { Dropdown, DropdownChangeEvent, globalToastService } from '@repo/atomic-components';
import { TemplateType } from '@/api';
import { themedDropdownPt } from '@/ui/components/themedDropdownPt';
import { GreenButton } from '@/ui/components/GreenButton';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { useUploadTemplate } from '../hooks';
import { DashboardSection } from './DashboardSection';

interface TemplateTypeOption {
  label: string;
  value: TemplateType;
}

const TEMPLATE_TYPE_OPTIONS: TemplateTypeOption[] = [
  { label: 'Chronology', value: TemplateType.CHRONOLOGY },
  { label: 'Index', value: TemplateType.INDEX },
];

// .docx templates only — matches the Word-document workflow in the guide.
const ACCEPTED_FILE_TYPES = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

interface UploadTemplateSectionProps {
  /** Called after a successful upload so the parent can refetch its lists. */
  onUploaded: (templateType: TemplateType) => void;
}

/**
 * Third dashboard section — uploads a new Chronology or Index template. Picks a
 * type, selects a .docx file, then runs the get-upload → blob PUT →
 * upload-complete flow via `useUploadTemplate`.
 */
export const UploadTemplateSection: FC<UploadTemplateSectionProps> = ({ onUploaded }) => {
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.CHRONOLOGY);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadTemplate, status, reset } = useUploadTemplate();
  const isBusy = status === 'preparing' || status === 'uploading' || status === 'completing';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const clearFileInput = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      globalToastService.show({ severity: 'info', summary: 'Uploading' });
      await uploadTemplate(templateType, selectedFile);
      globalToastService.show({ severity: 'success', summary: 'Uploaded' });
      clearFileInput();
      onUploaded(templateType);
    } catch {
      globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to upload template.' });
    }
  };

  return (
    <DashboardSection
      title="Upload new template"
      description="Add a custom Chronology or Index template (.docx). Tip: put the company logo in the Word header."
    >
      <div className="flex max-w-3xl flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="template-type" className="text-fletchers-gray-200 text-[14px]/[20px] font-[500]">
            Template type
          </label>
          <Dropdown
            id="template-type"
            value={templateType}
            options={TEMPLATE_TYPE_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(e: DropdownChangeEvent) => setTemplateType(e.value as TemplateType)}
            className="w-full max-w-xs"
            disabled={isBusy}
            {...themedDropdownPt()}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-fletchers-gray-200 text-[14px]/[20px] font-[500]">Template file</span>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileChange}
              disabled={isBusy}
              className="hidden"
            />
            <OutlineButton
              label="Choose file"
              icon="pi pi-file"
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}
              style={{ height: 36 }}
            />
            <span className="text-fletchers-black-900 max-w-xs truncate text-sm">
              {selectedFile ? selectedFile.name : 'No file selected'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <GreenButton
            label="Upload template"
            icon="pi pi-upload"
            disabled={!selectedFile || isBusy}
            loading={isBusy}
            onClick={handleUpload}
            style={{ height: 36 }}
          />
        </div>
      </div>
    </DashboardSection>
  );
};
